import { SAMPLE_COMPANIES } from '../../../components/FrostedGate/sample-companies';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import {
  CompanyPeriodRequestStatus,
  DataCollectionType,
  DataPointPeriodicity,
} from '../../../schemas/CompanyPeriodRequest.schema';
import { CompanyPeriodRequestManageViewModel, V2RequestType } from './company-period-request.view-model';
import { AvailablePeriod } from './manage-request-utils';

/**
 * A believable quarter of KPI requests for the frosted Manage Requests preview: a dozen
 * fictional companies spread across the statuses the page charts.
 */

const UNIVERSAL = 'Foresight Universal';
const FIRM = 'Quarterly Collection';

const Q2_2026 = '2026-06-30';
const Q1_2026 = '2026-03-31';
const Q4_2025 = '2025-12-31';

export const PREVIEW_COMPANIES = SAMPLE_COMPANIES;

export const PREVIEW_PERIODS: AvailablePeriod[] = [
  { value: Q2_2026, label: 'Q2 2026' },
  { value: Q1_2026, label: 'Q1 2026' },
  { value: Q4_2025, label: 'Q4 2025' },
];

interface PreviewSeed {
  status: CompanyPeriodRequestStatus;
  period: string;
  template: string;
  sentAt: string | null;
  dueDate: string | null;
  email: 'sent' | 'partial' | 'failed' | null;
}

const SEEDS: PreviewSeed[] = [
  {
    status: 'sent',
    period: Q2_2026,
    template: UNIVERSAL,
    sentAt: '2026-07-24',
    dueDate: '2026-08-29',
    email: 'sent',
  },
  {
    status: 'processing',
    period: Q2_2026,
    template: UNIVERSAL,
    sentAt: '2026-07-24',
    dueDate: '2026-08-29',
    email: 'sent',
  },
  {
    status: 'accepted',
    period: Q2_2026,
    template: FIRM,
    sentAt: '2026-07-15',
    dueDate: '2026-08-15',
    email: 'sent',
  },
  {
    status: 'sent',
    period: Q2_2026,
    template: UNIVERSAL,
    sentAt: '2026-07-24',
    dueDate: '2026-08-29',
    email: 'partial',
  },
  {
    status: 'processing',
    period: Q2_2026,
    template: FIRM,
    sentAt: '2026-07-15',
    dueDate: '2026-08-15',
    email: 'sent',
  },
  {
    status: 'accepted',
    period: Q1_2026,
    template: UNIVERSAL,
    sentAt: '2026-04-15',
    dueDate: '2026-05-15',
    email: 'sent',
  },
  {
    status: 'rejected',
    period: Q1_2026,
    template: UNIVERSAL,
    sentAt: '2026-04-15',
    dueDate: '2026-05-15',
    email: 'sent',
  },
  {
    status: 'sent',
    period: Q2_2026,
    template: FIRM,
    sentAt: '2026-07-15',
    dueDate: '2026-08-15',
    email: 'failed',
  },
  {
    status: 'accepted',
    period: Q4_2025,
    template: UNIVERSAL,
    sentAt: '2026-01-14',
    dueDate: '2026-02-13',
    email: 'sent',
  },
  {
    status: 'processing',
    period: Q1_2026,
    template: FIRM,
    sentAt: '2026-04-15',
    dueDate: '2026-05-15',
    email: 'sent',
  },
  {
    status: 'accepted',
    period: Q2_2026,
    template: UNIVERSAL,
    sentAt: '2026-07-24',
    dueDate: '2026-08-29',
    email: 'sent',
  },
  {
    status: 'sent',
    period: Q2_2026,
    template: UNIVERSAL,
    sentAt: '2026-07-24',
    dueDate: '2026-08-29',
    email: 'sent',
  },
];

/** One row per company, up to a dozen; companies beyond the seeds are left out. */
export function previewRequests(companies: ICompanyDataModel[]): CompanyPeriodRequestManageViewModel[] {
  return companies.slice(0, SEEDS.length).map((company, index) => {
    const seed = SEEDS[index];
    return {
      id: `00000000-0000-4000-8000-${String(index + 1).padStart(12, '0')}`,
      dataType: [DataCollectionType.kpi],
      period: seed.period,
      periodicity: DataPointPeriodicity.quarter,
      status: seed.status,
      maggieCompanyId: company.id,
      maggieCompanyName: company.name,
      _unmapped: false,
      companyName: company.name,
      companyId: company.id,
      fye: company.fye ?? 12,
      requestType: seed.template === UNIVERSAL ? V2RequestType.universal : V2RequestType.firm,
      templateName: seed.template,
      sentAt: seed.sentAt,
      dueDate: seed.dueDate,
      recipients: [],
      emailStatus: seed.email,
      emailFailedCount: seed.email === 'partial' || seed.email === 'failed' ? 1 : 0,
      emailTotalCount: seed.email === null ? 0 : 2,
    };
  });
}

/** Per-status counts plus the "All" total, the shape the menu bar and chart read. */
export function previewRequestStats(
  requests: CompanyPeriodRequestManageViewModel[]
): Map<CompanyPeriodRequestStatus | string, number> {
  const stats = new Map<CompanyPeriodRequestStatus | string, number>();
  for (const request of requests) {
    stats.set(request.status, (stats.get(request.status) ?? 0) + 1);
  }
  stats.set('All', requests.length);
  return stats;
}
